import { useEffect, useState } from 'react';
import { db } from '../data/db';
import { request } from '../lib/api';
import { nowIso } from '../lib/format';
import { isOnline } from './connectivity';

export type FileSource =
  | { kind: 'file'; fileId: string; name?: string }
  | { kind: 'document'; documentId: string; updatedAt: string; name?: string }
  | { kind: 'data-url'; dataUrl: string; name?: string }
  /** A compliance document uploaded on this device that is still waiting in the outbox. */
  | { kind: 'pending-document'; documentId: string; name?: string };

function cacheKey(source: FileSource) {
  if (source.kind === 'file') return source.fileId;
  if (source.kind === 'document') return `doc:${source.documentId}:${source.updatedAt}`;
  if (source.kind === 'pending-document') return `pending:${source.documentId}`;
  return '';
}

/**
 * Returns the file from this device if it has a copy (files captured here stay until uploaded, viewed files
 * are cached), otherwise downloads and caches it. Resolves null when offline without a local copy.
 */
export async function loadFile(source: FileSource): Promise<Blob | null> {
  if (source.kind === 'data-url') return (await fetch(source.dataUrl)).blob();
  if (source.kind === 'pending-document') {
    const ops = await db.outbox.where('entityKey').equals(`doc/${source.documentId}`).sortBy('seq');
    const upload = ops.reverse().find((op) => op.kind === 'document' && op.blobId);
    const stored = upload && upload.kind === 'document' && upload.blobId ? await db.blobs.get(upload.blobId) : undefined;
    if (stored) return stored.blob;
    // Only the details changed on this device; the file itself is the server's copy.
    const document = await db.documents.get(source.documentId);
    return document ? loadFile({ kind: 'document', documentId: source.documentId, updatedAt: document.updatedAt }) : null;
  }
  const key = cacheKey(source);
  const local = await db.blobs.get(key);
  if (local) return local.blob;
  if (!isOnline()) return null;
  const url = source.kind === 'file' ? `/api/files/${encodeURIComponent(source.fileId)}/content` : `/api/compliance-documents/${encodeURIComponent(source.documentId)}/content`;
  try {
    const blob = await (await request(url, { timeoutMs: 120_000 })).blob();
    if (source.kind === 'document') {
      const stale = await db.blobs.where('id').startsWith(`doc:${source.documentId}:`).primaryKeys();
      await db.blobs.bulkDelete(stale);
    }
    await db.blobs.put({ id: key, blob, name: source.name || key, type: blob.type, size: blob.size, createdAt: nowIso(), uploaded: true });
    return blob;
  } catch {
    return null;
  }
}

export type FileUrlState = { url: string; status: 'loading' | 'ready' | 'unavailable'; blob: Blob | null };

/** Object URL for a stored file, revoked automatically when the component unmounts or the source changes. */
export function useFileUrl(source: FileSource | null): FileUrlState {
  const [state, setState] = useState<FileUrlState>({ url: '', status: source ? 'loading' : 'unavailable', blob: null });
  const key = source ? (source.kind === 'data-url' ? source.dataUrl.slice(0, 64) + source.dataUrl.length : cacheKey(source)) : '';
  useEffect(() => {
    if (!source) { setState({ url: '', status: 'unavailable', blob: null }); return; }
    let cancelled = false; let url = '';
    setState((previous) => ({ ...previous, status: 'loading' }));
    loadFile(source).then((blob) => {
      if (cancelled) return;
      if (!blob) { setState({ url: '', status: 'unavailable', blob: null }); return; }
      url = URL.createObjectURL(blob);
      setState({ url, status: 'ready', blob });
    });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return state;
}

export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url; link.download = fileName;
  document.body.append(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
